// src/services/tables/dashboardColumns.js - DASHBOARD TABLE COLUMNS WITH PITCHER SUPPORT
import React from 'react';  // CRITICAL - MUST IMPORT REACT FOR JSX

// Import theme from parent directory colorService
import { dynastyTheme } from '../colorService';
import { formatBattingAvg, formatERA } from '../statFormatUtils';
import InjuryBadge from '../../components/InjuryBadge';
import PlayerLink from '../../components/PlayerLink';

// =============================================================================
// SHARED HELPERS
// =============================================================================

// Ownership badge: team name or "FA"
const ownerBadge = (p) => {
  const team = p?.league_context?.team?.team_name;
  if (team) {
    return React.createElement('span', {
      className: 'text-[9px] font-semibold px-1.5 py-0.5 rounded bg-yellow-400/15 text-yellow-400 truncate',
      style: { maxWidth: 70, display: 'inline-block' }
    }, team);
  }
  // No league_context means endpoint was called without league_id, or player truly is FA
  if (p?.league_context) {
    return React.createElement('span', {
      className: 'text-[9px] font-semibold px-1.5 py-0.5 rounded bg-green-500/15 text-green-400'
    }, 'FA');
  }
  return null;
};

// Player name cell with optional ownership badge
const playerCell = (p, rankColor) => React.createElement('div', { className: 'text-left' },
  React.createElement('div', { className: 'inline-flex items-center gap-1' },
    React.createElement(PlayerLink, { mlbId: p?.ids?.mlb, name: p?.info?.full_name || 'Unknown', style: { fontWeight: 600, color: '#f9fafb' } }),
    React.createElement(InjuryBadge, { mlbId: p?.ids?.mlb })
  ),
  React.createElement('div', {
    className: `text-xs ${dynastyTheme.classes.text.neutralLight} flex items-center gap-1`
  },
    React.createElement('span', null, `${p?.info?.position || 'POS'} • ${p?.info?.mlb_team || 'TM'}`),
    ownerBadge(p)
  )
);

// =============================================================================
// HITTER COLUMNS — R, HR, RBI, SB, AVG, OPS (HCML scoring categories)
// =============================================================================
export const createHotHittersColumns = () => [
  {
    key: 'rank', title: '#', width: 30,
    render: (_, p, i) => React.createElement('span', {
      className: `${dynastyTheme.classes.text.primary} font-bold`
    }, i + 1)
  },
  { key: 'name', title: 'Player', width: 160, render: (_, p) => playerCell(p) },
  { key: 'r', title: 'R', width: 35, render: (_, p) => (p?.stats?.rolling_7_day?.hitting?.runs || 0) },
  { key: 'hr', title: 'HR', width: 35, render: (_, p) => (p?.stats?.rolling_7_day?.hitting?.home_runs || 0) },
  { key: 'rbi', title: 'RBI', width: 35, render: (_, p) => (p?.stats?.rolling_7_day?.hitting?.rbi || 0) },
  { key: 'sb', title: 'SB', width: 35, render: (_, p) => (p?.stats?.rolling_7_day?.hitting?.stolen_bases || 0) },
  { key: 'avg', title: 'AVG', width: 50, render: (_, p) => formatBattingAvg(p?.stats?.rolling_7_day?.hitting?.batting_avg) },
  { key: 'ops', title: 'OPS', width: 55, render: (_, p) => formatBattingAvg(p?.stats?.rolling_7_day?.hitting?.ops) },
];

export const createColdHittersColumns = () => [
  {
    key: 'rank', title: '#', width: 30,
    render: (_, p, i) => React.createElement('span', {
      className: `${dynastyTheme.classes.text.neutralLight} font-bold`
    }, i + 1)
  },
  { key: 'name', title: 'Player', width: 160, render: (_, p) => playerCell(p) },
  { key: 'r', title: 'R', width: 35, render: (_, p) => (p?.stats?.rolling_7_day?.hitting?.runs || 0) },
  { key: 'hr', title: 'HR', width: 35, render: (_, p) => (p?.stats?.rolling_7_day?.hitting?.home_runs || 0) },
  { key: 'rbi', title: 'RBI', width: 35, render: (_, p) => (p?.stats?.rolling_7_day?.hitting?.rbi || 0) },
  { key: 'sb', title: 'SB', width: 35, render: (_, p) => (p?.stats?.rolling_7_day?.hitting?.stolen_bases || 0) },
  { key: 'avg', title: 'AVG', width: 50, render: (_, p) => formatBattingAvg(p?.stats?.rolling_7_day?.hitting?.batting_avg) },
  { key: 'ops', title: 'OPS', width: 55, render: (_, p) => formatBattingAvg(p?.stats?.rolling_7_day?.hitting?.ops) },
];

// =============================================================================
// PITCHER COLUMNS — W, SV, K, ERA, WHIP
// =============================================================================
export const createHotPitchersColumns = () => [
  {
    key: 'rank', title: '#', width: 30,
    render: (_, p, i) => React.createElement('span', {
      className: `${dynastyTheme.classes.text.primary} font-bold`
    }, i + 1)
  },
  { key: 'name', title: 'Player', width: 160, render: (_, p) => playerCell(p) },
  { key: 'wins', title: 'W', width: 35, render: (_, p) => (p?.stats?.rolling_7_day?.pitching?.wins || 0) },
  { key: 'saves', title: 'SV', width: 35, render: (_, p) => (p?.stats?.rolling_7_day?.pitching?.saves || 0) },
  { key: 'strikeouts', title: 'K', width: 35, render: (_, p) => (p?.stats?.rolling_7_day?.pitching?.strikeouts_pitched || 0) },
  { key: 'era', title: 'ERA', width: 50, render: (_, p) => formatERA(p?.stats?.rolling_7_day?.pitching?.era) },
  { key: 'whip', title: 'WHIP', width: 50, render: (_, p) => formatBattingAvg(p?.stats?.rolling_7_day?.pitching?.whip) },
];

export const createColdPitchersColumns = () => [
  {
    key: 'rank', title: '#', width: 30,
    render: (_, p, i) => React.createElement('span', {
      className: `${dynastyTheme.classes.text.neutralLight} font-bold`
    }, i + 1)
  },
  { key: 'name', title: 'Player', width: 160, render: (_, p) => playerCell(p) },
  { key: 'wins', title: 'W', width: 35, render: (_, p) => (p?.stats?.rolling_7_day?.pitching?.wins || 0) },
  { key: 'saves', title: 'SV', width: 35, render: (_, p) => (p?.stats?.rolling_7_day?.pitching?.saves || 0) },
  { key: 'strikeouts', title: 'K', width: 35, render: (_, p) => (p?.stats?.rolling_7_day?.pitching?.strikeouts_pitched || 0) },
  { key: 'era', title: 'ERA', width: 50, render: (_, p) => formatERA(p?.stats?.rolling_7_day?.pitching?.era) },
  { key: 'whip', title: 'WHIP', width: 50, render: (_, p) => formatBattingAvg(p?.stats?.rolling_7_day?.pitching?.whip) },
];

// Keep old names for backwards compatibility
export const createHotPlayersColumns = createHotHittersColumns;
export const createColdPlayersColumns = createColdHittersColumns;

// =============================================================================
// WAIVER WIRE COLUMNS
// =============================================================================
export const createWaiverAddsColumns = () => [
  {
    key: 'rank',
    title: '#',
    width: 35,
    render: (_, p, i) => React.createElement('span', {
      className: `${dynastyTheme.classes.text.success} font-bold`
    }, i + 1)
  },
  {
    key: 'name',
    title: 'Player',
    width: 140,
    render: (_, p) => React.createElement('div', { className: 'text-left' },
      React.createElement('div', { className: 'inline-flex items-center gap-1' },
        React.createElement(PlayerLink, { mlbId: p?.ids?.mlb, name: p?.info?.full_name || 'Unknown', style: { fontWeight: 600, fontSize: '0.875rem', color: '#f9fafb' } }),
        React.createElement(InjuryBadge, { mlbId: p?.ids?.mlb })
      ),
      React.createElement('div', {
        className: `text-xs ${dynastyTheme.classes.text.neutralLight}`
      }, `${p?.info?.position || 'POS'} • ${p?.info?.mlb_team || 'TM'}`)
    )
  },
  {
    key: 'adds_today',
    title: 'Adds',
    width: 60,
    render: (_, p) => React.createElement('span', {
      className: `${dynastyTheme.classes.text.success} font-bold`
    }, `+${p?.adds_today || 0}`)
  },
  { key: 'ownership', title: 'Own%', width: 60, render: (_, p) => (p?.ownership || '0%') },
  {
    key: 'trend',
    title: 'Δ',
    width: 55,
    render: (_, p) => React.createElement('span', {
      className: p?.trend?.startsWith('+') ? dynastyTheme.classes.text.success : dynastyTheme.classes.text.error
    }, p?.trend || '+0')
  }
];

export const createWaiverDropsColumns = () => [
  {
    key: 'rank',
    title: '#',
    width: 35,
    render: (_, p, i) => React.createElement('span', {
      className: `${dynastyTheme.classes.text.error} font-bold`
    }, i + 1)
  },
  {
    key: 'name',
    title: 'Player',
    width: 140,
    render: (_, p) => React.createElement('div', { className: 'text-left' },
      React.createElement('div', { className: 'inline-flex items-center gap-1' },
        React.createElement(PlayerLink, { mlbId: p?.ids?.mlb, name: p?.info?.full_name || 'Unknown', style: { fontWeight: 600, fontSize: '0.875rem', color: '#f9fafb' } }),
        React.createElement(InjuryBadge, { mlbId: p?.ids?.mlb })
      ),
      React.createElement('div', {
        className: `text-xs ${dynastyTheme.classes.text.neutralLight}`
      }, `${p?.info?.position || 'POS'} • ${p?.info?.mlb_team || 'TM'}`)
    )
  },
  {
    key: 'drops_today',
    title: 'Drops',
    width: 60,
    render: (_, p) => React.createElement('span', {
      className: `${dynastyTheme.classes.text.error} font-bold`
    }, `-${p?.drops_today || 0}`)
  },
  { key: 'ownership', title: 'Own%', width: 60, render: (_, p) => (p?.ownership || '0%') },
  {
    key: 'trend',
    title: 'Δ',
    width: 55,
    render: (_, p) => React.createElement('span', {
      className: dynastyTheme.classes.text.error
    }, p?.trend || '-0')
  }
];

// =============================================================================
// INJURY REPORT COLUMNS
// =============================================================================
export const createInjuryReportColumns = () => [
  {
    key: 'name',
    title: 'Player',
    width: 140,
    render: (_, p) => React.createElement('div', { className: 'text-left' },
      React.createElement(PlayerLink, { mlbId: p?.ids?.mlb, name: p?.info?.full_name || 'Unknown', style: { fontWeight: 600, fontSize: '0.875rem', color: '#f9fafb' } }),
      React.createElement('div', {
        className: `text-xs ${dynastyTheme.classes.text.neutralLight}`
      }, `${p?.info?.position || 'POS'} • ${p?.info?.mlb_team || 'TM'}`)
    )
  },
  {
    key: 'status',
    title: 'Status',
    width: 60,
    render: (_, p) => {
      const colors = {
        'IL-60': dynastyTheme.components.badge.error,
        'IL-15': dynastyTheme.components.badge.warning,
        'IL-10': dynastyTheme.components.badge.warning,
        'DTD': dynastyTheme.components.badge.info
      };
      return React.createElement('span', {
        className: colors[p?.status] || dynastyTheme.components.badge.neutral
      }, p?.status || 'Unknown');
    }
  },
  {
    key: 'injury',
    title: 'Injury',
    width: 150,
    render: (_, p) => React.createElement('span', {
      className: `text-xs ${dynastyTheme.classes.text.neutralLighter}`
    }, p?.injury || 'Unknown')
  },
  {
    key: 'return_date',
    title: 'Return',
    width: 80,
    render: (_, p) => React.createElement('span', {
      className: p?.return_date === '2026' ? dynastyTheme.classes.text.error : dynastyTheme.classes.text.neutralLight
    }, p?.return_date || 'TBD')
  }
];